import NoContentImg from "assets/images/defaults/no_challenges.png";
import { AxiosResponse } from "axios";
import { Filter } from "components/Filter";
import { Loading } from "components/loading";
import { NoContent } from "components/NoContent";
import { showDoubleActionPopUp } from "components/popup/doubleAction";
import { showToast } from "components/toast";
import { IFormFieldProperties } from "domain/feature/hotsite/Forms/contracts";
import { isEmptyField } from "domain/feature/hotsite/Forms/validations";
import { validateEmail } from "domain/feature/hotsite/Forms/validations/email";
import { BaseButton, Input } from "iebt-zing-storybook";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useParams } from "react-router";
import {
  DeleteFormat,
  deleteProvider,
  GetFormat,
  PostFormat,
  postProvider,
  useGet,
} from "sharedKernel/api";
import { hasTextMatch } from "sharedKernel/handlers/text";
import { trans } from "sharedKernel/i18n";
import {
  HubCompanyEvaluatorsInviteTable,
  HubCompanyEvaluatorsInviteTableProps,
} from "../invite/table";
import { EvaluatorInviteRow } from "../invite/table/contracts";

interface EvaluatorsData {
  id?: string;
  name: string;
  email: string;
  status: { code: string };
}

interface InviteEvaluatorResponse {
  name: string;
  email: string;
  status: string;
  message?: string;
}

export const HubCompanyEvaluatorsAdd = (): JSX.Element => {
  const initialFormFieldProperty = {
    value: "",
    isInvalid: false,
    isRequired: true,
    message: "",
  };

  const [tableData, setTableData] =
    useState<HubCompanyEvaluatorsInviteTableProps>({ header: [], rows: [] });
  const [newName, setNewName] = useState<IFormFieldProperties>(
    initialFormFieldProperty
  );
  const [newEmail, setNewEmail] = useState<IFormFieldProperties>(
    initialFormFieldProperty
  );
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");

  const labels = {
    pageTitle: trans("Gerenciar avaliadores"),
    name: trans("Avaliador"),
    email: trans("E-mail"),
    status: trans("Status"),
    actions: trans("Ações"),
    evaluatorNameInput: {
      title: trans("Nome do avaliador"),
      placeholder: trans("Insira o nome do(a) avaliador(a)"),
    },
    evaluatorEmailInput: {
      title: trans("E-mail"),
      placeholder: trans("Insira o e-mail do(a) avaliador"),
    },
    addButton: trans("Adicionar"),
    searchPlaceholder: trans("Insira algumas palavras chaves"),
    searchButton: trans("Buscar"),
    moreOptions: trans("Mais opções"),
    noEvaluators: trans("Ainda não há avaliadores para este evento."),
    removeTitle: trans("Remover avaliador"),
    removeMessage: trans("Tem certeza que deseja remover este avaliador do programa?"),
    notifyTitle: trans("Notificar novamente"),
    notifyMessage: trans("Tem certeza que deseja reenviar a notificação para este avaliador?"),
    yes: trans("Sim"),
    no: trans("Não"),
    removeSuccess: trans("Avaliador removido com sucesso"),
    notifySuccess: trans("Notificação reenviada com sucesso"),
    addSuccess: trans("Avaliador adicionado com sucesso!"),
    missingEvaluatorId: trans("Não foi possível identificar o avaliador"),
    requestError: trans("Erro ao processar a solicitação"),
  };

  const { challengeId } = useParams();

  const requestInvited: GetFormat = {
    url: `/v1/hub/company/challenge/${challengeId}/imported-evaluators?page=1`,
  };

  const {
    data: requestInvitedData,
    isFetching: isInvitedFetching,
    refetch: refetchInvited,
  } = useGet<EvaluatorsData[]>(requestInvited, "invited-evaluators-get");

  const mapEvaluatorToRow = (evaluator: EvaluatorsData): EvaluatorInviteRow => ({
    id: evaluator.id ?? "",
    name: evaluator.name,
    email: evaluator.email,
    status: evaluator.status.code,
  });

  useEffect(() => {
    if (!isInvitedFetching) {
      const invitedData = requestInvitedData.rows ?? [];
      setTableData({
        header: [labels.name, labels.status, labels.actions],
        rows: invitedData.map(mapEvaluatorToRow),
      });
    }
  }, [isInvitedFetching, requestInvitedData]);

  const onErrorInviteEvaluator = async (error: any): Promise<void> => {
    const message =
      error?.response?.data?.message ??
      error?.data?.message ??
      error?.message ??
      labels.requestError;
    showToast("error", message);
  };

  const onSuccessInviteEvaluator = async (
    response: AxiosResponse
  ): Promise<void> => {
    const evaluators: InviteEvaluatorResponse[] =
      response?.data?.data?.evaluators ?? [];
    const failedEvaluators = evaluators.filter(
      (evaluator) => evaluator.status === "error"
    );

    if (failedEvaluators.length > 0) {
      showToast(
        "error",
        failedEvaluators[0].message ?? labels.requestError
      );
      refetchInvited();
      return;
    }

    showToast("success", labels.addSuccess);
    refetchInvited();
    setNewName(initialFormFieldProperty);
    setNewEmail(initialFormFieldProperty);
  };

  const onSuccessRemove = async (): Promise<void> => {
    showToast("success", labels.removeSuccess);
    refetchInvited();
  };

  const onSuccessNotify = async (): Promise<void> => {
    showToast("success", labels.notifySuccess);
  };

  const onErrorAction = async (error: any): Promise<void> => {
    const status = error?.response?.status ?? error?.response?.data?.code;
    const message =
      error?.response?.data?.message ??
      error?.data?.message ??
      error?.message ??
      labels.requestError;
    showToast("error", status ? `(${status}) ${message}` : message);
  };

  const { mutate: inviteEvaluator } = postProvider(
    onSuccessInviteEvaluator,
    onErrorInviteEvaluator
  );
  const { mutate: removeEvaluator } = deleteProvider(onSuccessRemove, onErrorAction);
  const { mutate: notifyEvaluator } = postProvider(onSuccessNotify, onErrorAction);

  const handleAddEvaluator = (name, email): void => {
    const evaluatorsData = {
      evaluators: [
        {
          name,
          email,
        },
      ],
      challengeId: challengeId,
    };
    inviteEvaluator({
      url: `/v1/hub/company/challenge/${challengeId}/invite`,
      data: { data: evaluatorsData },
    });
  };

  const handleRemoveEvaluator = (evaluator: EvaluatorInviteRow): void => {
    if (!evaluator.id) {
      showToast("error", labels.missingEvaluatorId);
      return;
    }

    showDoubleActionPopUp({
      title: labels.removeTitle,
      message: labels.removeMessage,
      buttons: [{ text: labels.yes, value: "yes" }, { text: labels.no }],
      action: () => {
        const request: DeleteFormat = {
          url: `/v1/hub/company/challenge/${challengeId}/detach`,
          httpConfig: {
            data: {
              data: {
                evaluators: [{ id: evaluator.id }],
              },
            },
          },
        };
        removeEvaluator(request);
      },
    });
  };

  const handleNotifyAgain = (evaluator: EvaluatorInviteRow): void => {
    if (!evaluator.id) {
      showToast("error", labels.missingEvaluatorId);
      return;
    }

    showDoubleActionPopUp({
      title: labels.notifyTitle,
      message: labels.notifyMessage,
      buttons: [{ text: labels.yes, value: "yes" }, { text: labels.no }],
      action: () => {
        const request: PostFormat = {
          url: `/v1/hub/company/challenge/${challengeId}/evaluator/${evaluator.id}/notify`,
        };
        notifyEvaluator(request);
      },
    });
  };

  const onSearch = (searchTerm: string): void => {
    setCurrentSearchTerm(searchTerm);
  };

  const filteredRows = tableData.rows.filter(
    (row) =>
      hasTextMatch(row.name, currentSearchTerm) ||
      hasTextMatch(row.email ?? "", currentSearchTerm)
  );

  const getTableAreaElement = (): JSX.Element => {
    if (isInvitedFetching) {
      return (
        <div className="pt-5">
          <Loading />
        </div>
      );
    } else if (tableData.rows.length === 0) {
      return <NoContent text={labels.noEvaluators} image={NoContentImg} />;
    }
    return (
      <HubCompanyEvaluatorsInviteTable
        header={tableData.header}
        rows={filteredRows}
        onRemove={handleRemoveEvaluator}
        onNotifyAgain={handleNotifyAgain}
      />
    );
  };

  return (
    <>
      <Row className="d-flex align-items-center mb-3">
        <Col xs={5} sm={5} md={5} lg={5}>
          <Input
            id="evaluatorName"
            isRequired={true}
            value={newName.value}
            label={labels.evaluatorNameInput.title}
            placeholder={labels.evaluatorNameInput.placeholder}
            title={labels.evaluatorNameInput.placeholder}
            isInvalid={newName.isInvalid}
            notValidFieldMessage={newName.message}
            onChange={(event) => {
              setNewName({ ...newName, value: event.target.value });
            }}
            onBlur={() => {
              const newNameValidated = { ...newName };
              isEmptyField(newNameValidated);
              setNewName(newNameValidated);
            }}
          ></Input>
        </Col>
        <Col xs={5} sm={5} md={5} lg={5}>
          <Input
            id="evaluatorEmail"
            isRequired={true}
            value={newEmail.value}
            label={labels.evaluatorEmailInput.title}
            placeholder={labels.evaluatorEmailInput.placeholder}
            isInvalid={newEmail.isInvalid}
            notValidFieldMessage={newEmail.message}
            title={labels.evaluatorEmailInput.placeholder}
            onChange={(event) => {
              setNewEmail({
                ...newEmail,
                value: event.target.value.split(" ").join(""),
              });
            }}
            onBlur={() => {
              const newEmailValidated = { ...newEmail };
              const isEmptyEmail = isEmptyField(newEmailValidated);
              if (!isEmptyEmail) {
                validateEmail(newEmailValidated);
              }
              setNewEmail(newEmailValidated);
            }}
          ></Input>
        </Col>
        <Col xs={1} sm={2} md={2} lg={2} className="mb-0 pb-0">
          <BaseButton
            disabled={
              newName.isInvalid ||
              newEmail.isInvalid ||
              newName.value === "" ||
              newEmail.value === ""
            }
            btnContent={labels.addButton}
            variant="primary"
            size="big"
            onClick={() => handleAddEvaluator(newName.value, newEmail.value)}
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xs={12}>
          <Filter currentSearchTerm={currentSearchTerm} btnClick={onSearch} />
        </Col>
      </Row>
      {getTableAreaElement()}
    </>
  );
};
